// =============================================================================
// Sidebar — Modern operational navigation
// =============================================================================

import clsx from "clsx";

export interface SidebarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  badge?: string | number;
}

export interface SidebarSection {
  title?: string;
  items: SidebarItem[];
}

interface SidebarProps {
  sections: SidebarSection[];
  activeKey: string;
  collapsed: boolean;
  onToggleCollapse?: () => void;
  onSignOut?: () => void;
  signOutLabel?: string;
  workspaceLabel?: string;
  side?: "left" | "right";
  className?: string;
}

function CollapseToggle({
  collapsed,
  onToggle,
  side,
}: {
  collapsed: boolean;
  onToggle: () => void;
  side: "left" | "right";
}) {
  const isRtlSidebar = side === "right";

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      style={isRtlSidebar ? { left: "-14px" } : { right: "-14px" }}
      className="oelox-sidebar-toggle"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        style={{
          transform: collapsed
            ? isRtlSidebar
              ? "rotate(0deg)"
              : "rotate(180deg)"
            : isRtlSidebar
              ? "rotate(180deg)"
              : "rotate(0deg)",
        }}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

export function Sidebar({
  sections,
  activeKey,
  collapsed,
  onToggleCollapse,
  onSignOut,
  signOutLabel = "Log out",
  workspaceLabel,
  side = "left",
  className,
}: SidebarProps) {
  const isRight = side === "right";

  return (
    <aside
      style={{
        width: collapsed ? "var(--sidebar-collapsed)" : "var(--sidebar-width)",
        top: "var(--header-height)",
        bottom: 0,
        ...(isRight ? { right: 0 } : { left: 0 }),
      }}
      className={clsx(
        "oelox-sidebar fixed z-30 hidden md:flex flex-col transition-[width] duration-300 ease-in-out",
        isRight ? "oelox-sidebar--rtl" : "oelox-sidebar--ltr",
        className
      )}
    >
      {onToggleCollapse ? (
        <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} side={side} />
      ) : null}

      {!collapsed && workspaceLabel ? (
        <div className="oelox-sidebar__workspace">
          <p className="oelox-sidebar__workspace-label">{workspaceLabel}</p>
        </div>
      ) : null}

      <nav className="oelox-sidebar-nav flex-1 overflow-y-auto overflow-x-hidden py-2">
        {sections.map((section, si) => (
          <div key={si} className={clsx("oelox-sidebar__section", si > 0 && "mt-3")}>
            {section.title && !collapsed ? (
              <div className="oelox-sidebar__section-title">{section.title}</div>
            ) : null}
            {section.title && collapsed ? <div className="oelox-sidebar__section-rule" /> : null}

            {section.items.map((item) => {
              const active = activeKey === item.key;
              return (
                <div key={item.key} className="px-2 py-0.5">
                  <button
                    type="button"
                    onClick={item.onClick}
                    title={collapsed ? item.label : undefined}
                    className={clsx(
                      "oelox-sidebar__item group",
                      collapsed ? "oelox-sidebar__item--collapsed" : "oelox-sidebar__item--expanded",
                      active && "oelox-sidebar__item--active"
                    )}
                  >
                    <span className="oelox-sidebar__item-icon">{item.icon}</span>
                    {!collapsed ? (
                      <>
                        <span
                          className={clsx(
                            "oelox-sidebar__item-label",
                            isRight ? "text-right" : "text-left"
                          )}
                        >
                          {item.label}
                        </span>
                        {item.badge != null ? (
                          <span className="oelox-sidebar__badge">{item.badge}</span>
                        ) : null}
                      </>
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={clsx("oelox-sidebar__footer", collapsed && "oelox-sidebar__footer--collapsed")}>
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            title={collapsed ? signOutLabel : undefined}
            className={clsx(
              "oelox-sidebar__signout",
              collapsed ? "oelox-sidebar__signout--collapsed" : "oelox-sidebar__signout--expanded"
            )}
          >
            <span className="oelox-sidebar__item-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-[18px] w-[18px]"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </span>
            {!collapsed ? <span className="text-[13.5px] font-bold">{signOutLabel}</span> : null}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
